// Global
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { useTheme } from 'lib/context/ThemeContext';
import { Field, Item, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
// Components
import { ReviewsListTheme } from './ReviewsList.theme';
import { getReviewsDetails, parseReviewFilterData } from 'lib/reviews/get-reviews-v2';
import { ReviewDetails, ReviewProduct, SortBy } from 'lib/reviews/types';
import { format } from 'date-fns';
import { StarRating } from 'src/helpers/StarRating';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { Fragment, useEffect } from 'react';
import SvgIcon from '../SvgIcon/SvgIcon';
import { useReviewSummaryStore } from 'components/reviews/ReviewSummary/reviewsummary.store';
import RenderReviewImageSlider from './RenderReviewImageSlider.helper';

export type FetchReviewProps = {
  pageNumber: number;
  pageSize: number;
};

export type ReviewsListProps =
  Feature.EnterpriseWeb.RenewalByAndersen.Components.Reviews.ReviewSummaryAndList;

const ReviewsList = (props: ReviewsListProps) => {
  const { themeData } = useTheme(ReviewsListTheme);
  const { currentScreenWidth } = useCurrentScreenType();
  const numberOfReviews = props.fields?.reviewListPageSize?.value ?? 10;
  const loadMoreText = props?.fields?.loadMoreText?.value || 'Load More';
  const showProductImages: boolean = props?.fields?.showProductImages?.value;
  const sortField = props?.fields?.sortByOptions;
  const isMobile = currentScreenWidth < getBreakpoint('md');
  const starSize = isMobile ? '16' : '24';

  const { exactRating, setExactRating } = useReviewSummaryStore();
  const { pageNumber, setPageNumber } = useReviewSummaryStore();
  const { reviews, setReviews } = useReviewSummaryStore();
  const { sortOptions, setSortOptions } = useReviewSummaryStore();
  const { usingSupplementalReviews, setUsingSupplementalReviews } = useReviewSummaryStore();
  const { allFilters } = useReviewSummaryStore();

  const fetchData = async (
    fetchPageNumber: number,
    sortBy: SortBy[],
    supplementalReviewCount?: number
  ) => {
    return await getReviewsDetails(
      parseReviewFilterData({
        reviewFilterProps: {
          ...props,
          pageSize:
            // if we're getting supplemental reviews, we get the new count (explained below)
            supplementalReviewCount && supplementalReviewCount > 0
              ? supplementalReviewCount
              : numberOfReviews,
          pageNumber: fetchPageNumber,
          sortBy: sortBy,
          exactRating: exactRating,
        },
        reviewFilters: props.reviewsFilterRules,
      })
    );
  };

  useEffect(() => {
    const getRuleFilteredData = async (fetchPageNumber: number, sortBy: SortBy[]) => {
      const data = await fetchData(fetchPageNumber, sortBy);
      if (pageNumber === 1) {
        // if there are fewer than the pageSize (default 10) number of reviews, we will fetch
        // reviews again, but use a flag to exclude the affiliate, with the page size being
        // the difference between the original page size (example 10) and the original number of reviews
        // returned (example 1), which means we're looking for 9 extra reviews.
        if (data.length < numberOfReviews) {
          setUsingSupplementalReviews(true);
          const sortedList = data
            .concat(await fetchData(fetchPageNumber, sortBy, numberOfReviews - data.length))
            .sort((a, b) => (a?.review?.submissionDate < b?.review?.submissionDate ? 1 : -1));
          setReviews(sortedList);
          return;
        }
        setReviews(data);
      } else {
        setUsingSupplementalReviews(false);
        setReviews(reviews.concat(data));
      }
    };

    const getSummaryClickData = async (fetchPageNumber: number, sortBy: SortBy[]) => {
      setUsingSupplementalReviews(false);
      if (pageNumber === 1) {
        setReviews(await fetchData(fetchPageNumber, sortBy, undefined));
      } else {
        setReviews(reviews.concat(await fetchData(fetchPageNumber, sortBy, undefined)));
      }
    };

    if (allFilters) {
      getRuleFilteredData(pageNumber, sortOptions);
    } else {
      getSummaryClickData(pageNumber, sortOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOptions, pageNumber, exactRating]);

  const loadMoreClick = async () => {
    setPageNumber(pageNumber + 1);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sortChange = async (e: any) => {
    const sortField = e?.target?.selectedOptions[0]?.value;
    const sortFieldSplit = sortField.split(' ');
    if (sortFieldSplit.length === 2) {
      const newSortOption: SortBy[] = [
        {
          propertyName: sortFieldSplit[0],
          sortExpression: sortFieldSplit[1],
        },
      ];
      if (sortField.toLowerCase().includes('review.reviewrating')) {
        newSortOption.push({
          propertyName: 'review.submissionDate',
          sortExpression: 'DESC',
        });
      }
      setPageNumber(1);
      setExactRating(0);
      setSortOptions(newSortOption);
    }
  };

  const getUniqueProducts = (products: ReviewProduct[]) => {
    const uniqueProducts: ReviewProduct[] = [];
    products.forEach((prod) => {
      if (uniqueProducts.map((x) => x.productName).includes(prod.productName)) {
        return;
      }
      uniqueProducts.push(prod);
    });

    return props.fields?.product ? sortProducts(uniqueProducts) : uniqueProducts;
  };

  const sortProducts = (products: ReviewProduct[]) => {
    const sortedProducts: ReviewProduct[] = [];
    const currentProduct = products.filter(
      (x) =>
        x.productName.toLowerCase().replace('_', ' ') === props.fields?.product?.name.toLowerCase()
    );
    if (currentProduct.length === 1) {
      sortedProducts.push(currentProduct[0]);
      delete products[products.indexOf(currentProduct[0])];
    }
    products.forEach((prod) => {
      sortedProducts.push(prod);
    });
    return sortedProducts;
  };

  const revieListSliderSettings = {
    infinite: false,
    slidesToShow: 3,
    autoplay: false,
    arrows: true,
    dots: false,
    dotsClass: 'slick-dots static',
  };

  return (
    <div data-component="reviews/reviewslist" className={themeData.classes.reviewListWrapper}>
      {!usingSupplementalReviews && (
        <div className="float-right">
          <label className="text-xxs font-bold" htmlFor="sortOptions">
            SORT BY
          </label>
          <select
            name="sortOptions"
            title="Sort Options"
            className="border-none text-xs font-normal focus:shadow-none focus:ring-0 focus:ring-transparent focus:ring-offset-0 md:pr-ml"
            onChange={sortChange}
          >
            {sortField.map((_sortOption: Item) => {
              const name = _sortOption.name;
              const fieldValue = _sortOption.fields?.Value as Field;
              return (
                <Fragment key={_sortOption.id}>
                  <option value={fieldValue.value.toString()} key={_sortOption.id}>
                    {name}
                  </option>
                </Fragment>
              );
            })}
          </select>
        </div>
      )}
      <div className={themeData.classes.reviewList}>
        {reviews
          ?.filter((_review: ReviewDetails) => _review?.review !== undefined)
          .map((_review: ReviewDetails) => {
            const date = format(new Date(_review.review.submissionDate), 'MMMM Do YYYY');
            return (
              <Fragment key={_review.id}>
                <div className={themeData.classes.review}>
                  <div className={themeData.classes.ratingSection}>
                    <div className={themeData.classes.ratingText}>
                      <span aria-hidden="true">{_review.review.reviewRating}</span>
                    </div>
                    <div
                      className={themeData.classes.ratingStars}
                      role="img"
                      aria-label={`${_review.review.reviewRating} out of 5 stars`}
                    >
                      <StarRating
                        reviewStars={_review.review.reviewRating}
                        iconSize={starSize}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                  <div
                    className={
                      showProductImages
                        ? themeData.classes.reviewSection
                        : themeData.classes.reviewSectionNoProductImages
                    }
                  >
                    <div className={themeData.classes.reviewNameSection}>
                      <span className={themeData.classes.reviewName}>{_review.customerName}</span>
                      {_review?.city && _review?.state && (
                        <span className={themeData.classes.reviewLocation}>
                          &nbsp;from {_review.city}, {_review.state}
                        </span>
                      )}
                    </div>
                    <div className={themeData.classes.reviewDateSection}>
                      <span className={themeData.classes.reviewDate}>Posted on {date}</span>
                    </div>
                    <span
                      className={
                        showProductImages
                          ? themeData.classes.reviewText
                          : themeData.classes.reviewTextNoProductImages
                      }
                    >
                      {_review.review.reviewText}
                    </span>
                  </div>
                  {showProductImages && (
                    <div className={themeData.classes.productSection}>
                      <div>
                        <span className={themeData.classes.productSectionHeader}>
                          Product(s) installed
                        </span>
                      </div>
                      <div className="carousel-slider reviews-list-carousel-slider">
                        <div className={themeData.classes.productSectionImages}>
                          <RenderReviewImageSlider
                            sliderSettings={revieListSliderSettings}
                            slidesData={getUniqueProducts(_review.products as ReviewProduct[])}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Fragment>
            );
          })}
        {!usingSupplementalReviews && (
          <div className={themeData.classes.loadMoreContainer}>
            <button
              type="button"
              onClick={loadMoreClick}
              className={themeData.classes.loadMoreButton}
            >
              <span className={themeData.classes.loadMoreText}>{loadMoreText}</span>
              <SvgIcon icon="caret" size="lg" className="loadMoreIcon" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default withDatasourceCheck()<ReviewsListProps>(ReviewsList);
